/**
 * Session via context in react
 *
 * WARN - any change in the states used within the Provider component will
 * trigger a re-render across all the components that uses this context
 *
 * whats exported -
 * session - object (the last session, until updated)
 * demoUser - boolean (indicating demo user)
 * isMasterPasswordLogin - boolean (indicating master password login)
 * isAdminLogin - boolean (indicating admin login from JSCM)
 *
 */
import React, { useState, createContext, useContext, useEffect, useMemo, memo } from "react";
// import { unstable_createContext as routerCreateContext } from "react-router";

import { Session } from "@/session";
import { arrSigninAsAdminAuthId } from "@/config/Constants";
import { EventTrackerComponent } from "@/App";
import { getSessionDataNew } from "@/services/sessionServices";
import { AESGCM_DECRYPT } from "@/utils/aes-256-GCM";

const SessionContext = createContext({
  session: {},
  setSession: () => {},
  demoUser: false,
  isMasterPasswordLogin: false,
  isAdminLogin: false,
  isJSCDAdmin: false,
  isNewAddPostingsFlow: true,
});

export const useSessionContext = () => {
  const context = useContext(SessionContext);

  // return a safe default instead of react throwing error
  if (!context) {
    console.warn("useSessionContext called outside provider, returning default empty session.");
    return { session: {} };
  }

  return context;
};

/**
 * Provides a context for fetching the session
 * and a few important session properties.
 *
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The child components.
 * @returns {ReactNode} The context provider component.
 */
export const SessionContextProvider = memo(({ initSession = {}, children }) => {
  const [session, setSession] = useState(initSession);
  const demoUser = useMemo(() => Boolean(session?.UserEmail?.includes("demov2")), [session.UserEmail]);
  const isMasterPasswordLogin = useMemo(
    () => session?.LoginByMasterPass === true || false,
    [session.LoginByMasterPass]
  );
  const isAdminLogin = useMemo(
    () => arrSigninAsAdminAuthId?.includes(parseInt(session?.intAuthID || 0, 10)),
    [session.intAuthID]
  );
  const isJSCDAdmin = useMemo(() => session?.JSCDUserType === "Admin", [session.JSCDUserType]);
  const isNewAddPostingsFlow = useMemo(() => !!session?.UIPreferences?.SimplifySynonyms, [session.UIPreferences]);

  useEffect(() => {
    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
    let lastActivity = Date.now();
    let lastRefresh = Date.now();
    let isRefreshing = false;

    const refreshSession = async () => {
      if (isRefreshing) return;
      isRefreshing = true;

        try {
          const sessionData = await getSessionDataNew();
        if (sessionData?.session) {
          const decryptedString = AESGCM_DECRYPT(sessionData.session || "");
          setSession(JSON.parse(decryptedString));
          lastRefresh = Date.now();
        }
        } catch (error) {
          window.location.reload();
      } finally {
        isRefreshing = false;
        }
    };

    const handleActivity = () => {
      lastActivity = Date.now();
      // If user returns after being AFK and session is stale, refresh immediately
      if (Date.now() - lastRefresh > REFRESH_INTERVAL) {
        refreshSession();
      }
    };

    // Check periodically if we need to refresh (heartbeat for active users)
    const intervalId = setInterval(() => {
      const now = Date.now();
      const isUserActive = now - lastActivity < IDLE_TIMEOUT;
      const needsRefresh = now - lastRefresh > REFRESH_INTERVAL;

      if (isUserActive && needsRefresh) {
        refreshSession();
      }
    }, 60 * 1000); // Check every minute

    const events = ["mousedown", "keydown", "scroll", "touchstart", "focus"];
    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      clearInterval(intervalId);
      events.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, []);

  useEffect(() => {
    const isSessionValid = (sess) => {
      return [sess && Object.keys(sess).length > 0, sess && Object.keys(sess).length > 0 && sess.intAuthID];
    };

    const handleFocus = () => {
      const { payload } = Session.get("login") || {};
      const [wasValid, oldAuthID] = isSessionValid(session);
      const [isValid, currentAuthID] = isSessionValid(payload);

      if (wasValid !== isValid || oldAuthID !== currentAuthID) {
        window.location.reload();
      }
    };
    const handleSessionChange = (newSession) => {
      
      setSession(newSession.payload);
    };

    const unsubscribe = Session.onChange(handleSessionChange);

    window.addEventListener("focus", handleFocus);
    return () => {
      unsubscribe();

      window.removeEventListener("focus", handleFocus);
    };
  }, [session]);

  console.log("SessionContextProvider render with session - rerender", session);

  return (
    <SessionContext
      value={{
        session,
        setSession,
        demoUser,
        isMasterPasswordLogin,
        isAdminLogin,
        isJSCDAdmin,
        isNewAddPostingsFlow,
      }}
    >
      <EventTrackerComponent />
      {children}
    </SessionContext>
  );
});
